import { useState } from 'react'
import ECommerceHeader from '@/components/layout/ECommerceHeader'
import Footer from '@/components/layout/Footer'
import HeroCarousel from '@/components/carousel/HeroCarousel'
import TodayDealsSection from '@/components/deals/TodayDealsSection'
import CategoriesSection from '@/components/categories/CategoriesSection'
import ProductGrid from '@/components/product/ProductGrid'
import FilterSidebar from '@/components/filters/FilterSidebar'
import ProductDetailsModal from '@/components/product/ProductDetailsModal'
import { useCategories } from '@/hooks/useProducts'
import type { Product, ProductFilters } from '@/types/product'
import { Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'

export default function HomePage() {
  const [filters, setFilters] = useState<ProductFilters>({
    page: 1,
    limit: 20,
  })
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  const { data: categories } = useCategories()
  const categoryList = categories?.map(cat => ({ id: cat.id, name: cat.name })) || []

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product)
  }

  const handleFiltersChange = (newFilters: ProductFilters) => {
    setFilters(newFilters)
  }

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page })
  }

  return (
    <div className="min-h-screen bg-background">
      <ECommerceHeader />

      <main>
        {/* Hero Carousel */}
        <section className="container mx-auto px-4 py-6">
          <HeroCarousel />
        </section>

        {/* Today's Deals */}
        <TodayDealsSection />

        {/* Categories */}
        <CategoriesSection />

        {/* All Products with Sidebar Filter */}
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">All Products</h2>
              <div className="flex items-center gap-3">
                {/* Mobile Filter Toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  className="lg:hidden"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                </Button>
                <select
                  className="px-4 py-2 rounded-md border border-input bg-background text-foreground text-sm"
                  value={filters.sort || 'relevance'}
                  onChange={(e) => handleFiltersChange({ ...filters, sort: e.target.value as any, page: 1 })}
                >
                  <option value="relevance">Sort by: Relevance</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="rating_desc">Top Rated</option>
                  <option value="created_desc">Newest First</option>
                  <option value="discount_desc">Best Discount</option>
                </select>
              </div>
            </div>

            <div className="grid lg:grid-cols-[280px_1fr] gap-6">
              {/* Sidebar Filter - Desktop */}
              <div className="hidden lg:block">
                <FilterSidebar
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  categories={categoryList}
                />
              </div>

              {/* Mobile Filter Collapsible */}
              {showFilters && (
                <div className="lg:hidden mb-4">
                  <FilterSidebar
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    categories={categoryList}
                  />
                </div>
              )}

              {/* Product Grid */}
              <div>
                <ProductGrid
                  filters={filters}
                  onProductClick={handleProductClick}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Product Details Modal */}
      <ProductDetailsModal
        product={selectedProduct}
        open={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />
    </div>
  )
}
